'use client';

import React, { type CSSProperties } from 'react';
import {
  ATHLYN_VIEWBOX,
  ATHLYN_BODY_PATHS,
  ATHLYN_ACCENT_PATHS,
} from './athlynLogoPaths';

/**
 * AthlynLogo — the Athlyn monogram (geometric "A" with a lime accent).
 *
 * Vector-traced from the brand master (brand/logo/athlyn-logo-source.png) so it
 * stays crisp at every size. The mark body is `currentColor` by default, so it
 * adapts to light/dark surfaces automatically — set the parent's `color` (or
 * pass `color`). The lime accent stays fixed unless `accent` is overridden.
 *
 * `size` is the rendered HEIGHT in px; width follows the mark's aspect ratio.
 * Path geometry lives in ./athlynLogoPaths (shared with the OG image).
 */

const LIME = '#9BC22E';

export interface AthlynLogoProps {
  /** Rendered height in px. Default 32. */
  size?: number;
  /** Mark body color. Default `currentColor` (inherits the parent's color). */
  color?: string;
  /** Accent color. Default brand lime. */
  accent?: string;
  /** Accessible label; when set the SVG becomes role="img", else aria-hidden. */
  title?: string;
  style?: CSSProperties;
}

export function AthlynLogo({
  size = 32,
  color = 'currentColor',
  accent = LIME,
  title,
  style,
}: AthlynLogoProps) {
  const width = Math.round(size * (ATHLYN_VIEWBOX.w / ATHLYN_VIEWBOX.h));
  return (
    <svg
      width={width}
      height={size}
      viewBox={`0 0 ${ATHLYN_VIEWBOX.w} ${ATHLYN_VIEWBOX.h}`}
      fill="none"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      style={{ display: 'block', ...style }}
    >
      {title ? <title>{title}</title> : null}
      {ATHLYN_BODY_PATHS.map((d, i) => (
        <path key={`b${i}`} d={d} fill={color} />
      ))}
      {ATHLYN_ACCENT_PATHS.map((d, i) => (
        <path key={`a${i}`} d={d} fill={accent} />
      ))}
    </svg>
  );
}
